import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import type { MethodTypeKind } from '@protocols/use-cases';

/**
 * The icon a method type is drawn with, by the kind of its asset type.
 *
 * Presentation data, so it stays in the UI library — the domain layer has no
 * awareness that a UI exists. It lives on this component rather than in a shared
 * constants module because the fallback below is a rule, and a rule needs an
 * owner: exported as a bare map, every screen re-implemented the lookup and the
 * copies disagreed on what an unresolved kind should render.
 */
const METHOD_TYPE_ICONS: Record<MethodTypeKind, string> = {
  image: 'ri-camera-2-line',
  video: 'ri-film-line',
  audio: 'ri-mic-line',
  text: 'ri-file-text-line',
};

/** Stand-in for a method type whose kind is unresolved (or not created yet). */
const UNKNOWN_METHOD_TYPE_ICON = 'ri-add-circle-line';

/**
 * Renders the icon for a method type's kind, wherever a method type is listed.
 *
 * Dumb by construction: one input, no output, no injected facade, state or
 * effects. It exists so a method type cannot look broken on one screen and fine
 * on another — the map AND its unresolved-kind fallback have exactly one home.
 *
 * Sizing and colour come from the consumer's own stylesheet through this
 * element's class, which the icon glyph inherits.
 */
// No `styleUrls`: sizing and colour are deliberately inherited from the
// consumer's own class on this element (see the class doc above), so this
// component owns no styles of its own and an empty stylesheet would only mislead.
@Component({
  selector: 'ts-method-type-icon',
  templateUrl: './method-type-icon.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
})
export class MethodTypeIconComponent {
  /**
   * The method type's kind. Absent, null, or a value the map does not know all
   * render the placeholder — a row whose kind could not be resolved must look the
   * same on every screen.
   */
  @Input() kind: MethodTypeKind | undefined | null;

  public get icon(): string {
    if (this.kind === undefined || this.kind === null) return UNKNOWN_METHOD_TYPE_ICON;
    return METHOD_TYPE_ICONS[this.kind] ?? UNKNOWN_METHOD_TYPE_ICON;
  }
}
